import React, { useRef, useState } from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';

import type { HeroVideoButton, HeroVideoProps } from 'src/components/molecules/HeroVideo/HeroVideo';
import { HeroVideo } from 'src/components/molecules/HeroVideo/HeroVideo';
import { HeroVideoTakeover } from 'src/components/molecules/HeroVideoTakeover/HeroVideoTakeover';
import { SectionContainer } from 'src/components/molecules/SectionContainer/SectionContainer';
import type { HeroVideoCtaPlacement } from 'src/tracking/components/heroVideo';
import {
  trackHeroVideoCtaClick,
  trackHeroVideoImpression,
} from 'src/tracking/components/heroVideo';
import * as styles from './HeroVideoSection.style';

export type { HeroVideoButton };

// Tracking is owned here, not in the molecules: they expose callbacks and stay GA-agnostic.
export interface HeroVideoSectionProps
  extends Omit<HeroVideoProps, 'onCtaClick' | 'onWatchNowClick' | 'videoTitle'> {
  endScreenHeading?: string;
  expandedPlaybackId: string;
  isFirstSection?: boolean;
  onCollapse?: () => void;
  onVideoComplete?: () => void;
  onWatchNowClick?: () => void;
  trackingPosition?: number;
  trackingTitle?: string;
  /**
   * The Contentstack entry title. GA4 gets it as is; Mux Data gets it suffixed per player so the
   * preview loop and the full film stay separate rows. Assets are keyed by video_id there anyway.
   */
  videoTitle: string;
}

export const HeroVideoSection = ({
  endScreenHeading,
  expandedPlaybackId,
  isFirstSection,
  onCollapse,
  onVideoComplete,
  onWatchNowClick,
  trackingPosition,
  trackingTitle,
  videoTitle,
  ...heroProps
}: HeroVideoSectionProps) => {
  const [takeoverOpen, setTakeoverOpen] = useState(false);
  const triggerRef = useRef<HTMLElement | null>(null);
  const trackingContext = {
    trackingPosition,
    trackingTitle: trackingTitle ?? '',
    videoTitle,
  };
  const sectionRef = useImpressionRef<HTMLDivElement>(() =>
    trackHeroVideoImpression(trackingContext),
  );
  const onCtaClick =
    (placement: HeroVideoCtaPlacement) =>
    (button: HeroVideoButton, e: React.MouseEvent<HTMLAnchorElement>) =>
      trackHeroVideoCtaClick(e, trackingContext, button.text, placement);

  const openTakeover = () => {
    triggerRef.current = document.activeElement as HTMLElement;
    onWatchNowClick?.();
    setTakeoverOpen(true);
  };

  const closeTakeover = () => {
    setTakeoverOpen(false);
    onCollapse?.();
    triggerRef.current?.focus();
  };

  return (
    <div css={styles.container}>
      <SectionContainer
        ref={sectionRef}
        wrapperStyles={isFirstSection ? styles.firstSectionWrapper : styles.wrapper}
      >
        <>
          <HeroVideo
            {...heroProps}
            onCtaClick={onCtaClick('inline')}
            onWatchNowClick={openTakeover}
            paused={takeoverOpen}
            videoTitle={`${videoTitle} | preview`}
          />
          {takeoverOpen && (
            <HeroVideoTakeover
              buttons={heroProps.buttons}
              endScreenHeading={endScreenHeading}
              expandedPlaybackId={expandedPlaybackId}
              onCollapse={closeTakeover}
              onCtaClick={onCtaClick('end screen')}
              onVideoComplete={onVideoComplete}
              videoTitle={`${videoTitle} | film`}
            />
          )}
        </>
      </SectionContainer>
    </div>
  );
};
